/**
 * Map window.dataLayer push items to B2M track() payloads.
 * Keeps backend contract unchanged: flat params + items[] (BulkAPI flattens to item_N_*).
 *
 * No event-name filtering: whatever dataLayer pushes (including gtm.* and
 * GTM command arrays) is mapped into our payload shape and forwarded.
 */

const EVENT_NAME_KEYS = new Set(['event', 'event_name', 'eventName']);

const ECOMMERCE_SCALAR_KEYS = [
  'value',
  'currency',
  'transaction_id',
  'tax',
  'shipping',
  'coupon',
  'affiliation',
  'shipping_tier',
  'payment_type',
  'item_list_id',
  'item_list_name',
  'creative_name',
  'creative_slot',
  'promotion_id',
  'promotion_name'
];

/**
 * Safe param key: dots in GTM keys (gtm.elementClasses) → underscores.
 * @param {string} key
 * @returns {string}
 */
function sanitizeKey(key) {
  return String(key).replace(/\./g, '_');
}

/**
 * Top-level / nested keys treated as user props (CRM + device-like fields sites may push).
 * Unknown ecommerce/event keys stay on the event payload.
 */
const USER_PROP_KEY_ALIASES = {
  userId: 'user_id',
  userID: 'user_id',
  uid: 'user_id',
  customer_id: 'user_id',
  customerId: 'user_id',
  external_id: 'user_id',
  externalId: 'user_id',
  user_email: 'email',
  userEmail: 'email',
  e_mail: 'email',
  phone_number: 'phone',
  user_phone: 'phone',
  userPhone: 'phone',
  full_name: 'name',
  user_name: 'name',
  userName: 'name',
  username: 'name',
  screen: 'screen_resolution',
  screenResolution: 'screen_resolution',
  screen_size: 'screen_resolution',
  // OS aliases stay on `os` — never map into `platform` (SDK surface = "Web").
  operating_system: 'os',
  fingerprint: 'fingerprint_id',
  fingerprintId: 'fingerprint_id',
  device_fingerprint: 'fingerprint_id'
};

const USER_PROP_KEYS = new Set([
  // CRM / identity
  'user_id', 'email', 'phone', 'name', 'first_name', 'last_name',
  'company', 'company_id', 'plan', 'subscription', 'role',
  // Device / browser (same keys SDK puts in user_params)
  'user_agent', 'language', 'platform', 'os', 'os_version', 'browser', 'browser_version',
  'device_type', 'screen_resolution', 'color_depth',
  'timezone', 'timezone_offset', 'hardware_concurrency', 'device_memory',
  'is_touch_capable', 'app_identifier', 'fingerprint_id',
  'webgl_vendor', 'canvas_hash',
  '$fbp', '$fbc', '$gclid', '$ga_client_id',
  // raw aliases also accepted before normalization
  ...Object.keys(USER_PROP_KEY_ALIASES)
]);

const USER_NESTED_OBJECTS = [
  'user', 'user_properties', 'userProperties', 'user_data', 'userData', 'visitor'
];

/**
 * Normalize a single user-prop key to canonical form.
 * @param {string} key
 * @returns {string}
 */
function canonicalizeUserPropKey(key) {
  const k = sanitizeKey(key);
  return USER_PROP_KEY_ALIASES[key] || USER_PROP_KEY_ALIASES[k] || k;
}

/**
 * Copy plain scalar/object values into target under canonical keys.
 * @param {object} source
 * @param {object} target
 */
function assignUserProps(source, target) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return;
  Object.keys(source).forEach((key) => {
    const val = source[key];
    if (val === undefined || val === null || val === '') return;
    if (typeof val === 'object') {
      // Nested CRM blobs — flatten one level into user props
      if (!Array.isArray(val)) {
        Object.keys(val).forEach((nk) => {
          const nv = val[nk];
          if (nv === undefined || nv === null || nv === '') return;
          if (typeof nv === 'object') return;
          target[canonicalizeUserPropKey(nk)] = nv;
        });
      }
      return;
    }
    target[canonicalizeUserPropKey(key)] = val;
  });
}

/**
 * Extract user-prop candidates from a raw dataLayer item.
 * Used in datalayer/hybrid: DL values win; SDK fills gaps later in BulkAPI.
 * @param {any} item
 * @returns {object}
 */
export function extractUserPropsFromDataLayerItem(item) {
  const out = {};
  if (!item || typeof item !== 'object' || Array.isArray(item)) return out;

  // Nested user / user_properties / user_data objects
  USER_NESTED_OBJECTS.forEach((nk) => {
    if (item[nk] && typeof item[nk] === 'object' && !Array.isArray(item[nk])) {
      assignUserProps(item[nk], out);
    }
  });

  // Top-level known user keys
  Object.keys(item).forEach((key) => {
    if (!USER_PROP_KEYS.has(key) && !USER_PROP_KEY_ALIASES[key]) return;
    if (USER_NESTED_OBJECTS.includes(key)) return;
    const val = item[key];
    const isJunk = val === null || val === '' ||
      (typeof val === 'string' &&
        ['undefined', 'null'].includes(val.trim().toLowerCase()));
    if (isJunk) {
      // An EXPLICIT null/empty user_id — or its templating artifacts
      // "undefined"/"null" — is a logout signal: carry it through so the
      // merge drops the sticky login id instead of silently keeping the
      // previous user's identity on every later event.
      if (canonicalizeUserPropKey(key) === 'user_id') out.user_id = null;
      return;
    }
    if (val === undefined) return;
    if (typeof val === 'object') return;
    out[canonicalizeUserPropKey(key)] = val;
  });

  return out;
}

/**
 * Serialize a value for event_params (primitives kept; objects/arrays JSON).
 * @param {any} value
 * @returns {any}
 */
function serializeValue(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'object') return value;

  // DOM nodes / window objects — store a short descriptor
  if (typeof Element !== 'undefined' && value instanceof Element) {
    return {
      tag: value.tagName,
      id: value.id || undefined,
      className: value.className || undefined
    };
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    try {
      return String(value);
    } catch (e2) {
      return undefined;
    }
  }
}

/**
 * Resolve event name from a dataLayer item (GTM / GA4 / loose aliases / command arrays).
 * No gtm.* filtering — returns null only when there is no usable name.
 * @param {any} item
 * @returns {string|null}
 */
export function extractEventName(item) {
  if (item == null) return null;

  // GTM command arrays: ["set", ...], ["consent", "update", {...}]
  if (Array.isArray(item)) {
    if (item.length === 0) return null;
    const cmd = item[0];
    if (cmd == null || (typeof cmd !== 'string' && typeof cmd !== 'number')) return null;
    return String(cmd);
  }

  if (typeof item !== 'object') return null;

  const name = item.event || item.event_name || item.eventName;
  if (!name || (typeof name !== 'string' && typeof name !== 'number')) return null;
  return String(name);
}

/**
 * Normalize product/item objects to B2M / GA4 item shape.
 * @param {Array|any} items
 * @returns {Array<object>}
 */
export function normalizeItems(items) {
  if (!items) return [];
  const list = Array.isArray(items) ? items : [items];

  return list.map((item, index) => {
    if (!item || typeof item !== 'object') return null;
    return {
      item_id: item.id || item.item_id || item.product_id || item.sku || item.itemId,
      item_name: item.name || item.item_name || item.product_name || item.title || item.itemName,
      item_brand: item.brand || item.item_brand || item.vendor,
      item_category: item.category || item.item_category || item.product_type || item.itemCategory,
      item_category2: item.item_category2 || item.category2,
      item_category3: item.item_category3 || item.category3,
      item_variant: item.variant || item.item_variant,
      price: item.price != null ? parseFloat(item.price) || 0 : undefined,
      quantity: item.quantity != null ? parseInt(item.quantity, 10) || 1 : 1,
      index: item.position != null ? item.position : (item.index != null ? item.index : index),
      coupon: item.coupon,
      discount: item.discount,
      currency: item.currency
    };
  }).filter(Boolean);
}

/**
 * Map GA4 / Enhanced Ecommerce `ecommerce` object into flat B2M params.
 * @param {object} ecommerce
 * @param {object} params - mutated
 */
function applyEcommerceObject(ecommerce, params) {
  if (!ecommerce || typeof ecommerce !== 'object') return;

  if (Object.keys(ecommerce).length === 0) return;

  for (const key of ECOMMERCE_SCALAR_KEYS) {
    if (ecommerce[key] !== undefined && ecommerce[key] !== null) {
      params[key] = ecommerce[key];
    }
  }

  if (Array.isArray(ecommerce.items) && ecommerce.items.length > 0) {
    params.items = normalizeItems(ecommerce.items);
    params.item_count = params.items.length;
  }

  if (ecommerce.impressions) {
    params.items = normalizeItems(ecommerce.impressions);
    params.item_count = params.items.length;
  }
  if (ecommerce.detail?.products) {
    params.items = normalizeItems(ecommerce.detail.products);
    params.item_count = params.items.length;
  }
  if (ecommerce.click?.products) {
    params.items = normalizeItems(ecommerce.click.products);
    params.item_count = params.items.length;
  }
  if (ecommerce.add?.products) {
    params.items = normalizeItems(ecommerce.add.products);
    params.item_count = params.items.length;
  }
  if (ecommerce.remove?.products) {
    params.items = normalizeItems(ecommerce.remove.products);
    params.item_count = params.items.length;
  }
  if (ecommerce.checkout?.products) {
    params.items = normalizeItems(ecommerce.checkout.products);
    params.item_count = params.items.length;
  }
  if (ecommerce.purchase?.products) {
    const action = ecommerce.purchase.actionField || {};
    if (action.id != null) params.transaction_id = action.id;
    if (action.revenue != null) params.value = action.revenue;
    if (action.tax != null) params.tax = action.tax;
    if (action.shipping != null) params.shipping = action.shipping;
    if (action.coupon != null) params.coupon = action.coupon;
    if (action.affiliation != null) params.affiliation = action.affiliation;
    params.items = normalizeItems(ecommerce.purchase.products);
    params.item_count = params.items.length;
  }
  if (ecommerce.currencyCode && params.currency == null) {
    params.currency = ecommerce.currencyCode;
  }
  if (ecommerce.currency && params.currency == null) {
    params.currency = ecommerce.currency;
  }
}

/**
 * Flatten a plain object one level with prefix.
 * @param {object} obj
 * @param {string} prefix
 * @param {object} target
 */
function flattenShallow(obj, prefix, target) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
  Object.keys(obj).forEach((key) => {
    const val = obj[key];
    if (val === undefined) return;
    const outKey = sanitizeKey(`${prefix}_${key}`);
    if (val !== null && typeof val === 'object') {
      const serialized = serializeValue(val);
      if (serialized !== undefined) target[outKey] = serialized;
      return;
    }
    target[outKey] = val;
  });
}

/**
 * Map GTM command arrays (Arguments / Array push style) to B2M payload.
 * @param {Array} item
 * @returns {{ eventName: string, params: object }|null}
 */
function mapCommandArray(item) {
  const eventName = extractEventName(item);
  if (!eventName) return null;

  const params = {
    source: 'datalayer',
    gtm_command: eventName
  };

  if (item.length > 1) {
    const arg1 = item[1];
    if (arg1 !== undefined) {
      if (arg1 !== null && typeof arg1 === 'object' && !Array.isArray(arg1)) {
        flattenShallow(arg1, 'arg1', params);
      } else {
        params.gtm_command_arg = serializeValue(arg1);
      }
    }
  }

  if (item.length > 2) {
    const arg2 = item[2];
    if (arg2 !== undefined) {
      if (arg2 !== null && typeof arg2 === 'object' && !Array.isArray(arg2)) {
        flattenShallow(arg2, 'consent', params);
        params.gtm_command_payload = serializeValue(arg2);
      } else {
        params.gtm_command_value = serializeValue(arg2);
      }
    }
  }

  // Keep remaining args as JSON for completeness
  if (item.length > 3) {
    params.gtm_command_args = serializeValue(item.slice(1));
  }

  return { eventName, params };
}

/**
 * Map a dataLayer item to `{ eventName, params }` for B2M track().
 * Returns null only when the item cannot yield an event name.
 * @param {any} item
 * @returns {{ eventName: string, params: object }|null}
 */
export function mapDataLayerItem(item) {
  if (Array.isArray(item)) {
    return mapCommandArray(item);
  }

  const eventName = extractEventName(item);
  if (!eventName) return null;

  const params = {
    source: 'datalayer'
  };

  // Copy all top-level fields (including gtm.*) into params
  Object.keys(item).forEach((key) => {
    if (EVENT_NAME_KEYS.has(key)) return;
    if (key === 'ecommerce') return; // handled below → B2M items shape

    const val = item[key];
    if (val === undefined) return;

    const outKey = sanitizeKey(key);

    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      // Prefer shallow flatten for nested plain objects; also keep JSON blob
      flattenShallow(val, outKey, params);
      const serialized = serializeValue(val);
      if (serialized !== undefined && Object.keys(serialized).length > 0) {
        // Avoid huge duplicate when flatten already expanded scalars
        if (!Object.keys(params).some((k) => k.startsWith(outKey + '_'))) {
          params[outKey] = serialized;
        }
      }
      return;
    }

    if (Array.isArray(val)) {
      params[outKey] = serializeValue(val);
      return;
    }

    params[outKey] = val;
  });

  // Ecommerce → B2M items / scalars
  if (item.ecommerce && typeof item.ecommerce === 'object') {
    applyEcommerceObject(item.ecommerce, params);
  }

  if (typeof window !== 'undefined' && window.location) {
    if (params.page_url == null) params.page_url = window.location.href;
    if (params.page_path == null) params.page_path = window.location.pathname;
  }

  return { eventName, params };
}

export default {
  extractEventName,
  extractUserPropsFromDataLayerItem,
  normalizeItems,
  mapDataLayerItem
};
